import React, { useEffect, useState } from "react";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import {
  DatasetMetaSection,
  MetaAddButton,
} from "@/components/main/datasets/DatasetMetaSection";
import { Button } from "components/common/Button";
import { formatGuideSize } from "@/components/main/datasets/maintenanceGuideFormat";
import { MaintenanceGuideDialog } from "@/components/main/datasets/MaintenanceGuideDialog";
import {
  getMaintenanceGuideMeta,
  type MaintenanceGuideMeta,
} from "@/lib/api/datasets";

const daysAgo = (unixMillis: number): string => {
  const days = Math.floor((Date.now() - unixMillis) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  return new Date(unixMillis).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

interface MaintenanceGuideSectionProps {
  datasetId: string;
  datasetName: string;
  canManage: boolean;
  /// The parent row's expanded state — the meta fetch waits for it, so the
  /// guide costs nothing on a dataset list load.
  expanded: boolean;
  pr?: string;
}

/// The doorway row for a dataset's maintenance guide: maintainer-only, and a
/// `DatasetMetaSection` with NO children, so it structurally cannot expand —
/// the 10 KB body lives behind the dialog, never inline. Status comes from
/// the `/meta` endpoint, which never decrypts.
export const MaintenanceGuideSection: React.FC<
  MaintenanceGuideSectionProps
> = ({ datasetId, datasetName, canManage, expanded, pr }) => {
  // `undefined` = not fetched yet, `null` = fetched, no guide.
  const [meta, setMeta] = useState<MaintenanceGuideMeta | null | undefined>(
    undefined,
  );
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (!expanded || !canManage || meta !== undefined) return;
    let cancelled = false;
    getMaintenanceGuideMeta(datasetId)
      .then((m) => {
        if (!cancelled) setMeta(m);
      })
      // Treat a failed status read as "no guide" — the dialog still works.
      .catch(() => {
        if (!cancelled) setMeta(null);
      });
    return () => {
      cancelled = true;
    };
  }, [expanded, canManage, meta, datasetId]);

  if (!canManage) return null;

  const refreshMeta = () => setMeta(undefined);

  return (
    <>
      <DatasetMetaSection
        icon={<MenuBookOutlinedIcon className="icon-sm" />}
        label="Maintenance guide"
        pr={pr}
        meta={
          meta ? (
            <span
              className="text-fg-muted truncate"
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "var(--font-size-small)",
              }}
            >
              · updated {daysAgo(meta.updated_at)}
              {meta.updated_by_display ? ` by ${meta.updated_by_display}` : ""}
              {" · "}
              {formatGuideSize(meta.bytes)}
            </span>
          ) : undefined
        }
        action={
          meta ? (
            <Button
              variant="default"
              size="sm"
              onClick={() => setDialogOpen(true)}
            >
              Open
            </Button>
          ) : (
            <MetaAddButton
              label="Write maintenance guide"
              onClick={() => setDialogOpen(true)}
            />
          )
        }
      />
      <MaintenanceGuideDialog
        datasetId={datasetId}
        datasetName={datasetName}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={refreshMeta}
      />
    </>
  );
};
